import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Skeleton} from 'src/design/atoms/Skeleton';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Tooltip} from 'src/design/atoms/Tooltip';
import {useMemo} from 'react';
import {useGetUserActivityDetailQuery} from 'src/redux/queries/userActivity';

export interface UserActivityHeatmapProps {
  userId: number;
}

const RECT_SIZE = 16;
const GAP = 3;
const WEEKDAY_LABELS = ['Mon', '', 'Wed', '', 'Fri', '', ''];
const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

const formatDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;

const formatDisplayDate = (d: Date): string =>
  d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

interface DayCell {
  date: Date;
  dateStr: string;
  active: boolean;
  weekday: number; // 0=Mon, 6=Sun
  weekIndex: number;
}

function buildGrid(
  startDate: Date,
  endDate: Date,
  activeDates: Set<string>
): {
  cells: DayCell[];
  weeks: number;
  monthMarkers: {label: string; weekIndex: number}[];
} {
  const cells: DayCell[] = [];

  // Adjust start to Monday
  const start = new Date(startDate);
  const dayOfWeek = start.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
  start.setDate(start.getDate() + mondayOffset);

  const current = new Date(start);
  const end = new Date(endDate);
  let weekIndex = 0;
  let lastMonth = -1;
  const monthMarkers: {label: string; weekIndex: number}[] = [];

  while (current <= end) {
    const weekday = (current.getDay() + 6) % 7; // 0=Mon, 6=Sun
    if (weekday === 0 && cells.length > 0) {
      weekIndex++;
    }

    const month = current.getMonth();
    if (month !== lastMonth) {
      monthMarkers.push({label: MONTH_LABELS[month], weekIndex});
      lastMonth = month;
    }

    const dateStr = formatDate(current);
    cells.push({
      date: new Date(current),
      dateStr,
      active: activeDates.has(dateStr),
      weekday,
      weekIndex,
    });

    current.setDate(current.getDate() + 1);
  }

  return {cells, weeks: weekIndex + 1, monthMarkers};
}

export const UserActivityHeatmap: React.FC<UserActivityHeatmapProps> = ({
  userId,
}) => {
  const endDate = useMemo(() => new Date(), []);
  const startDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d;
  }, []);

  const {data, isLoading, isError} = useGetUserActivityDetailQuery(
    {userId, from: startDate, to: endDate},
    {refetchOnMountOrArgChange: true}
  );

  const {cells, weeks, monthMarkers} = useMemo(() => {
    const activeDateSet = new Set(
      (data?.activeDates ?? []).map(d => formatDate(new Date(d)))
    );
    return buildGrid(startDate, endDate, activeDateSet);
  }, [data, startDate, endDate]);

  if (isLoading) {
    return <Skeleton height={120} />;
  }

  if (isError) {
    return (
      <Text c="dimmed" size="sm">
        Failed to load activity data.
      </Text>
    );
  }

  const weekdayLabelWidth = 36;
  const svgWidth = weekdayLabelWidth + weeks * (RECT_SIZE + GAP);
  const monthLabelHeight = 16;
  const svgHeight = monthLabelHeight + 7 * (RECT_SIZE + GAP);

  return (
    <Stack gap="sm">
      <Box style={{overflowX: 'auto'}}>
        <svg width={svgWidth} height={svgHeight} role="img">
          {monthMarkers.map((m, i) => (
            <text
              key={i}
              x={weekdayLabelWidth + m.weekIndex * (RECT_SIZE + GAP)}
              y={12}
              fontSize={12}
              fill="var(--mantine-color-dimmed)">
              {m.label}
            </text>
          ))}
          {WEEKDAY_LABELS.map(
            (label, i) =>
              label && (
                <text
                  key={i}
                  x={0}
                  y={monthLabelHeight + i * (RECT_SIZE + GAP) + RECT_SIZE - 1}
                  fontSize={12}
                  fill="var(--mantine-color-dimmed)">
                  {label}
                </text>
              )
          )}
          {cells.map(cell => (
            <Tooltip
              key={cell.dateStr}
              label={`${formatDisplayDate(cell.date)}: ${
                cell.active ? 'Active' : 'Inactive'
              }`}
              withArrow
              position="top"
              fz="xs">
              <rect
                x={weekdayLabelWidth + cell.weekIndex * (RECT_SIZE + GAP)}
                y={monthLabelHeight + cell.weekday * (RECT_SIZE + GAP)}
                width={RECT_SIZE}
                height={RECT_SIZE}
                rx={2}
                fill={
                  cell.active
                    ? 'var(--mantine-color-green-6)'
                    : 'var(--mantine-color-dark-1)'
                }
              />
            </Tooltip>
          ))}
        </svg>
      </Box>
      <Group gap="xs" justify="flex-end">
        <Box
          w={RECT_SIZE}
          h={RECT_SIZE}
          style={{
            borderRadius: 2,
            backgroundColor: 'var(--mantine-color-green-6)',
          }}
        />
        <Text size="xs" c="dimmed">
          Active
        </Text>
        <Box
          w={RECT_SIZE}
          h={RECT_SIZE}
          style={{
            borderRadius: 2,
            backgroundColor: 'var(--mantine-color-dark-1)',
          }}
        />
        <Text size="xs" c="dimmed">
          Inactive
        </Text>
      </Group>
    </Stack>
  );
};
